import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { apiLogin, apiMe, formatApiError } from '../lib/api';

export default function Login() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isLockedOut, setIsLockedOut] = useState(false);
  const [lockoutCountdown, setLockoutCountdown] = useState(0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      // Step 1: Login and get tokens
      await apiLogin(email, password);

      // Step 2: Fetch authenticated user profile from /api/auth/me
      const user = await apiMe();

      // Step 3: Update auth context with user
      setUser(user);

      // Step 4: Navigate to dashboard
      navigate('/');
    } catch (err: any) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || 'Login failed';

      if (status === 429) {
        setError('Too many failed attempts. Please try again later.');
        setIsLockedOut(true);

        const retryAfter = err.response?.headers?.['retry-after'];
        const seconds = retryAfter ? parseInt(retryAfter, 10) : 60;
        setLockoutCountdown(seconds);

        const interval = setInterval(() => {
          setLockoutCountdown((prev) => {
            if (prev <= 1) {
              clearInterval(interval);
              setIsLockedOut(false);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        setError(formatApiError(detail));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="flex h-screen w-screen items-center justify-center"
      style={{
        backgroundImage: 'url(https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?auto=format&fit=crop&q=80&w=2564)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
      data-testid="login-page"
    >
      {/* macOS-style login card */}
      <div
        className="bg-white/95 rounded-2xl shadow-lg border border-gray-200 p-10 w-full max-w-sm"
        style={{ backdropFilter: 'none' }}
      >
        {/* Logo / Branding */}
        <div className="flex flex-col items-center mb-8">
          <div
            className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4"
            style={{ backgroundColor: '#BDC3C7' }}
          >
            <span className="text-white font-bold text-xl tracking-tight">K</span>
          </div>
          <h1 className="text-xl font-bold text-gray-900 tracking-tight">KAELO</h1>
          <p className="text-xs text-gray-500 mt-1 font-medium">Kinetic Agent Executive for Layer Oversight</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wider">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-400 transition-shadow"
              data-testid="login-email-input"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wider">
              Password
            </label>
            <div className="relative">
              <input
                type={showPw ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-400 pr-10 transition-shadow"
                data-testid="login-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPw(!showPw)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                data-testid="login-toggle-password"
              >
                {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </div>

          {error && (
            <p className="text-xs text-red-500 font-medium" data-testid="login-error">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading || isLockedOut}
            className="w-full rounded-lg py-2.5 text-sm font-semibold text-white transition-all active:scale-95 disabled:opacity-60"
            style={{ backgroundColor: isLockedOut ? '#ef4444' : '#111827' }}
            data-testid="login-submit-button"
          >
            {isLockedOut
              ? `Try again in ${lockoutCountdown}s`
              : loading
                ? 'Signing in...'
                : 'Sign in'}
          </button>
        </form>

        <p className="text-center text-xs text-gray-400 mt-6">
          KAELO HQ — Restricted Access
        </p>
      </div>
    </div>
  );
}
